import { VersionedEntityKind } from "./versioned-entity-config";
import type { PromptFilterValue, VersionedEntityFilterValue, WorkflowFilterValue } from "./types";

export const ANY_VERSION_VALUE = "__any_version__";
export const ANY_VERSION_LABEL = "Any version";

export const isPromptFilterValue = (value: unknown): value is PromptFilterValue =>
  typeof value === "object" &&
  value !== null &&
  "prompt_id" in value &&
  typeof value.prompt_id === "number" &&
  (!("prompt_version_number" in value) ||
    value.prompt_version_number === undefined ||
    typeof value.prompt_version_number === "number");

export const isWorkflowFilterValue = (value: unknown): value is WorkflowFilterValue =>
  typeof value === "object" &&
  value !== null &&
  "workflow_id" in value &&
  typeof value.workflow_id === "number" &&
  (!("workflow_version_number" in value) ||
    value.workflow_version_number === undefined ||
    typeof value.workflow_version_number === "number");

export const isVersionedEntityFilterValue = (value: unknown): value is VersionedEntityFilterValue =>
  isPromptFilterValue(value) || isWorkflowFilterValue(value);

export const isVersionedEntityFilterValueList = (
  value: unknown,
): value is VersionedEntityFilterValue[] =>
  Array.isArray(value) && value.every(item => isVersionedEntityFilterValue(item));

export const getVersionedEntityId = (value: VersionedEntityFilterValue): number =>
  isPromptFilterValue(value) ? value.prompt_id : value.workflow_id;

export const getVersionedEntityVersionNumber = (
  value: VersionedEntityFilterValue,
): number | undefined =>
  isPromptFilterValue(value) ? value.prompt_version_number : value.workflow_version_number;

export const buildVersionRef = (entityId: number, versionNumber: number): string =>
  `${entityId}:${versionNumber}`;

export const getVersionedEntitySelectionKey = (value: VersionedEntityFilterValue): string => {
  const entityId = getVersionedEntityId(value);
  const versionNumber = getVersionedEntityVersionNumber(value);
  return `${entityId}:${versionNumber ?? "any"}`;
};

export const createVersionedEntityFilterValue = (
  kind: VersionedEntityKind,
  entityId: number,
  versionNumber?: number,
): VersionedEntityFilterValue => {
  if (kind === VersionedEntityKind.Prompt) {
    return versionNumber === undefined
      ? { prompt_id: entityId }
      : { prompt_id: entityId, prompt_version_number: versionNumber };
  }

  return versionNumber === undefined
    ? { workflow_id: entityId }
    : { workflow_id: entityId, workflow_version_number: versionNumber };
};

export const parseVersionedEntityFilterValue = (
  kind: VersionedEntityKind,
  value: string | object,
): VersionedEntityFilterValue | null => {
  if (typeof value === "object" && isVersionedEntityFilterValue(value)) {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }

    if (kind === VersionedEntityKind.Prompt && isPromptFilterValue(parsed)) {
      return parsed;
    }

    if (kind === VersionedEntityKind.Workflow && isWorkflowFilterValue(parsed)) {
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
};

export const isStaleEntityListSuggestionOnVersionStep = (
  suggestion: { value: string; prompt_name?: string | null; label?: string | null },
  versionStep: boolean,
  currentEntityId?: number,
): boolean => {
  if (!versionStep) {
    return false;
  }

  if (suggestion.prompt_name || suggestion.label) {
    return true;
  }

  return currentEntityId !== undefined && suggestion.value === String(currentEntityId);
};

export const formatVersionedEntitySuggestionLabel = ({
  suggestion,
  versionStep,
}: {
  suggestion: { value: string; label?: string | null; prompt_name?: string | null };
  versionStep: boolean;
}): string => {
  if (versionStep) {
    if (suggestion.value === ANY_VERSION_VALUE) {
      return ANY_VERSION_LABEL;
    }

    return `v${suggestion.value}`;
  }

  return suggestion.label ?? suggestion.prompt_name ?? suggestion.value;
};

export const formatVersionedEntityDisplayLabel = (
  kind: VersionedEntityKind,
  entityName: string,
  versionNumber?: number,
): string => {
  if (versionNumber === undefined) {
    return `${entityName} (${ANY_VERSION_LABEL})`;
  }

  return `${entityName} v${versionNumber}`;
};
